import * as React from "react";
import { IFtthOrchestrator } from "@nju/data-access-portal-orchestrator";
import { PaymentState } from "@nju/data-access-portal-payment";
import { IScope } from "@nju/scope";
import { BlockText, Loader, Modal, Separator, Text, useOverlay } from "@nju/ui";
import { formatPrice } from "@nju/utils";
import { PaymentStatus } from "./PaymentStatus";
import { SwitchFtthPaymentModal } from "./SwitchFtthPaymentModal";

interface ISidebarPaymentDetailsProps {
  state: IFtthOrchestrator["state"] | "REACTIVATION" | undefined;
  orderSummary: IFtthOrchestrator["orderSummary"];
  isLoading: boolean | undefined;
  isPending: boolean;
  paymentState: PaymentState | undefined;
  variant: IScope;
  ftthOrchestrator?: IFtthOrchestrator;
  promoOrchestrator?: IFtthOrchestrator;
}

export function SidebarFtthPaymentDetails({
  state,
  orderSummary,
  isLoading,
  isPending,
  paymentState,
  variant,
  promoOrchestrator,
  ftthOrchestrator,
}: ISidebarPaymentDetailsProps) {
  const modal = useOverlay();
  const voucherPromoCode =
    promoOrchestrator?.voucherPromoCode || ftthOrchestrator?.voucherPromoCode;

  const openModal = React.useCallback(() => {
    modal.open();
  }, [modal]);

  if (orderSummary === undefined) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  const showChangePaymentButton =
    (state && ["ORDER_SUMMARY", "REACTIVATION"].includes(state)) || false;

  const sfh = orderSummary.items.find((item) => {
    return item.offerId === "NJU_FTTH_SFH" || item.offerId === "P-NJU-SFH";
  });

  const ftthSubscription = orderSummary.items.find((item) => {
    return (
      item.offerId === "NJU_FTTH_Subscription" ||
      item.offerId === "P-NJU-Internet"
    );
  });

  const activationFee =
    orderSummary.items.find((item) => {
      return item.offerId === "P-NJU-FTTH-Activation-Payment";
    })?.offerPrice ?? 0;

  const isActivationFeeVisible =
    ftthOrchestrator?.type === "NJU_FTTH_ONBOARDING" && activationFee > 0;

  const installationFee =
    orderSummary.items.find((item) => {
      return item.offerId === "P-NJU-Assistance";
    })?.offerPrice ?? 0;

  return (
    <>
      {state === "REACTIVATION" && (
        <>
          <div className="grid gap-2 grid-cols-[2fr_1fr] pt-3">
            {ftthSubscription && (
              <>
                <Text>opłata miesięczna:</Text>
                <Text align="right">
                  {formatPrice(ftthSubscription.offerPrice)} zł
                </Text>
              </>
            )}
            {sfh && (
              <>
                <Text>dopłata miesięczna w budynku jednorodzinnym:</Text>
                <Text align="right">{formatPrice(sfh.offerPrice)} zł</Text>
              </>
            )}
          </div>
          <Separator size="s" thickness="thin" tone="complementary" />
        </>
      )}
      {state !== "REACTIVATION" && (
        <>
          <div className="grid gap-2 grid-cols-[3fr_1fr] pt-3">
            {isActivationFeeVisible && (
              <>
                <Text>jednorazowa opłata aktywacyjna:</Text>
                <Text align="right">{formatPrice(activationFee)} zł</Text>
              </>
            )}
            <Text>jednorazowa opłata za instalację:</Text>
            <Text align="right">{formatPrice(installationFee)} zł</Text>
          </div>
          {(ftthSubscription || sfh) && (
            <Separator size="s" thickness="medium" tone="complementary" />
          )}
          <div className="grid gap-2 grid-cols-[3fr_1fr]">
            {ftthSubscription && (
              <>
                <Text>opłata miesięczna:</Text>
                <Text align="right">
                  {formatPrice(ftthSubscription.offerPrice)} zł
                </Text>
              </>
            )}
            {sfh && (
              <>
                <Text>dopłata miesięczna w budynku jednorodzinnym:</Text>
                <Text align="right">{formatPrice(sfh.offerPrice)} zł</Text>
              </>
            )}
          </div>
          <Separator size="s" thickness="medium" tone="complementary" />
        </>
      )}
      {isLoading && (
        <>
          <div className="flex justify-center">
            <Loader />
          </div>
          <Separator size="xs" thickness="medium" />
        </>
      )}
      {paymentState && (
        <>
          <PaymentStatus
            isPending={isPending}
            openModal={openModal}
            paymentState={paymentState}
            showChangePaymentButton={showChangePaymentButton}
            variant={variant}
          />
          <Separator size="m" thickness="medium" />
        </>
      )}
      {state === "REACTIVATION" && (
        <>
          <div className="grid gap-2 grid-cols-[2fr_1fr]">
            <Text>do zapłaty:</Text>
            <Text align="right">
              {formatPrice(orderSummary.totalRecurringOrderPrice)} zł
            </Text>
          </div>
          <Separator size="s" tone={"invisible"} />
        </>
      )}
      {state !== "REACTIVATION" && (
        <div className="grid gap-2 grid-cols-[2fr_1fr]">
          <Text>pierwsza opłata:</Text>
          <Text align="right">
            {formatPrice(
              promoOrchestrator?.orderSummary?.totalOrderPrice ??
                orderSummary.totalOrderPrice,
            )}{" "}
            zł
          </Text>
          {!voucherPromoCode?.includes("OFFER1M") && (
            <>
              <Text>kolejne opłaty:</Text>
              <Text align="right">
                {formatPrice(
                  promoOrchestrator?.orderSummary?.totalRecurringOrderPrice ??
                    orderSummary.totalRecurringOrderPrice,
                )}{" "}
                zł
              </Text>
            </>
          )}
          {(promoOrchestrator?.orderSummary
            ?.totalRecurringOrderPriceAfterPromotion ||
            orderSummary?.totalRecurringOrderPriceAfterPromotion) && (
            <>
              <Text>opłata po okresie promocyjnym:</Text>
              <Text align="right">
                {formatPrice(
                  (promoOrchestrator?.orderSummary
                    ?.totalRecurringOrderPriceAfterPromotion ??
                    orderSummary?.totalRecurringOrderPriceAfterPromotion) as number,
                )}{" "}
                zł
              </Text>
            </>
          )}
        </div>
      )}
      {state === "REACTIVATION" && (
        <BlockText>
          Usługa zostanie włączona po potwierdzeniu płatności.
        </BlockText>
      )}
      {modal.isOpened && (
        <Modal onClose={modal.close}>
          <SwitchFtthPaymentModal
            closeModal={() => {
              return modal.close();
            }}
          />
        </Modal>
      )}
    </>
  );
}
